import { Record } from 'immutable';
import { format, parseISO } from 'date-fns';
import { jobHelpers } from '@saucelabs/sl-util-helpers';
import { constants as cloudTypeConstants } from '@saucelabs/sl-module-cloudtype';
import capitalize from 'lodash.capitalize';

import { COVERAGE_TYPES } from './constants';

export const MetricsRecord = new Record({
  name: '',
  title: '',
  value: null,
  previousValue: null,
  labelClassName: '',
});

const TestCasesClass = new Record({
  name: null,
  total_runs: null,
  avg_duration: null,
  pass_rate: null,
  fail_rate: null,
  error_rate: null,
  complete_rate: null,
  median_duration: null,
  total_duration: null,
  statuses: {
    passed: 0,
    error: 0,
    complete: 0,
    failed: 0,
  },
});

const getFormattedDuration = (value) => (
  jobHelpers.getDuration(value)
);

const getFormattedRate = (value) => (
  `${(value * 100).toFixed(1)}%`
);

export class TestCaseRecord extends TestCasesClass {
  getDuration() {
    return jobHelpers.getDuration(this.avg_duration, 1);
  }

  getMedianDuration() {
    return getFormattedDuration(this.median_duration);
  }

  getTotalDuration() {
    return getFormattedDuration(this.total_duration);
  }

  getPassRatePercentage() {
    return getFormattedRate(this.pass_rate);
  }

  getFailRatePercentage() {
    return getFormattedRate(this.fail_rate);
  }

  getErrorRatePercentage() {
    return getFormattedRate(this.error_rate);
  }

  getCompleteRatePercentage() {
    return getFormattedRate(this.complete_rate);
  }

  getStatusCount(status) {
    return this.statuses?.[status] || 0;
  }
}

const TestRecordClass = new Record({
  id: null,
  consolidated_status: null,
  creation_time: null,
  duration_in_progress_sec: null,
  os_normalized: null,
  browser_normalized: null,
});

export class TestRecord extends TestRecordClass {
  formatCreationTime() {
    return format(parseISO(this.creation_time), 'yyyy-MM-dd HH:MM:ss');
  }
}

export const FailureTestRecord = new Record({
  id: null,
  name: null,
  description: null,
  testCount: null,
  rootCauseUrl: null,
  framework: null,
  loading: true,
  rootCauses: [],
});

export const CoverageRecordClass = new Record({
  type: null,
  cloudType: null,
  name: null,
  count: null,
  total_duration: null,
  getName() {
    return this.name;
  },
});

export class CoverageRecord extends CoverageRecordClass {
  /**
   * Get usageScore(from 0 to 100) on the base of maxCount of coverage
   * @param {number} maxCount
   * @returns {number}
   */
  getScore(maxCount) {
    const usageScore = maxCount ? parseInt((this.count / maxCount) * 100, 10) : 0;
    if (usageScore < 0) {
      return 0;
    }

    if (usageScore > 100) {
      return 100;
    }

    return usageScore;
  }

  /**
   * Get colorCode (from dsBlue50 to dsBlue900) according to usageScore
   * @param {number} maxCount
   * @returns {string}
   */
  getColorCode(maxCount) {
    const colorSuffix = Math.floor(this.getScore(maxCount) / 10) * 100;
    if (colorSuffix > 900) {
      return 'dsBlue900';
    }

    if (colorSuffix < 100) {
      return 'dsBlue50';
    }

    return `dsBlue${colorSuffix}`;
  }

  getDuration() {
    return getFormattedDuration(this.total_duration);
  }

  /**
   * Convert coverage count number to comma separated string
   * @returns {number|string}
   */
  getCountLocalString() {
    let countLocalString = this.count;
    try {
      countLocalString = countLocalString.toLocaleString('en-US');
    } catch (error) {
      return countLocalString;
    }
    return countLocalString;
  }
}

export class VdcDeviceCoverageRecord extends CoverageRecord {
  constructor(props = {}) {
    super({
      ...props,
      type: COVERAGE_TYPES.DEVICE,
      cloudType: cloudTypeConstants.CLOUD_VIRTUAL,
    });
  }
}

export class RdcDeviceCoverageRecord extends CoverageRecord {
  constructor(props = {}) {
    super({
      ...props,
      type: COVERAGE_TYPES.DEVICE,
      cloudType: cloudTypeConstants.CLOUD_REAL,
    });
  }
}

export class VdcBrowserCoverageRecord extends CoverageRecord {
  constructor(props = {}) {
    super({
      ...props,
      type: COVERAGE_TYPES.BROWSER,
      cloudType: cloudTypeConstants.CLOUD_VIRTUAL,
    });
  }
}

export class VdcOSCoverageRecord extends CoverageRecord {
  constructor(props = {}) {
    super({
      ...props,
      type: COVERAGE_TYPES.OS,
      cloudType: cloudTypeConstants.CLOUD_VIRTUAL,
    });
  }
}

export class RdcOSCoverageRecord extends CoverageRecord {
  constructor(props = {}) {
    super({
      ...props,
      type: COVERAGE_TYPES.OS,
      cloudType: cloudTypeConstants.CLOUD_REAL,
    });
  }

  getName() {
    if (this.name) {
      return capitalize(this.name).replace(/ios/i, 'iOS');
    }

    return '';
  }
}
